import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { UserPlus, Trophy, Warning, Confetti } from "@phosphor-icons/react";
import confetti from "canvas-confetti";

export default function JoinInvite() {
  const { code } = useParams();
  const nav = useNavigate();
  const { setUserAndToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  useEffect(() => {
    api.get(`/invite/${code}`)
      .then((r) => setPreview(r.data))
      .finally(() => setLoading(false));
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/invite/${code}/accept`, form);
      if (data.token) localStorage.setItem("cfn_token", data.token);
      if (setUserAndToken) setUserAndToken(data.user);
      confetti({ particleCount: 180, spread: 90, origin: { y: 0.6 }, colors: ["#2563eb", "#34d399", "#2563eb", "#fb7185", "#09090b"] });
      toast.success(`Welcome to ${data.mp_name}'s team!`);
      nav("/");
      // Force refresh if context hook wasn't available
      setTimeout(() => window.location.reload(), 400);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not accept invite");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="brut px-8 py-4 font-display font-black text-xl">LOADING…</div>
      </div>
    );
  }

  if (!preview?.valid) {
    return (
      <div className="min-h-screen bg-[#f4f4f5] flex items-center justify-center p-6">
        <div className="brut bg-white p-8 max-w-md w-full text-center" data-testid="invite-invalid">
          <div className="w-14 h-14 border-2 border-black bg-[#fb7185] mx-auto mb-4 flex items-center justify-center">
            <Warning size={28} weight="bold" color="#fff" />
          </div>
          <div className="font-display font-black text-2xl tracking-tighter">Invite Unavailable</div>
          <p className="text-zinc-600 mt-2">This invite link is invalid or has been revoked by the Managing Partner.</p>
          <Link to="/login" className="inline-block mt-6 border-2 border-black px-4 py-2 font-bold uppercase tracking-widest text-xs hover:bg-[#2563eb]">
            Go to Sign In →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f4f5] flex items-stretch">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 bg-[#09090b] text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes opacity-[0.06]" />
        <div className="relative z-10">
          <div className="inline-flex items-center gap-3 border-2 border-white px-3 py-2">
            <div className="w-8 h-8 bg-[#2563eb] border-2 border-white" />
            <span className="font-display font-black tracking-tight text-xl">CFN</span>
          </div>
          <div className="mt-16">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#2563eb] mb-4">You're invited</div>
            <h1 className="font-display font-black text-5xl xl:text-6xl leading-[0.95] tracking-tighter">
              JOIN<br />
              <span className="text-[#2563eb]">{preview.mp_name}'s</span><br />
              TEAM
            </h1>
            <p className="mt-8 max-w-md text-zinc-300 text-lg leading-relaxed">
              Creative Financial Network. Track carrier appointments, log sales, compete with teammates.
            </p>
          </div>
        </div>
        <div className="relative z-10 grid grid-cols-2 gap-4">
          <div className="border-2 border-white px-4 py-3 flex items-center gap-3">
            <Trophy size={22} weight="bold" />
            <span className="font-bold text-sm tracking-widest">LEADERBOARD</span>
          </div>
          <div className="border-2 border-white px-4 py-3 flex items-center gap-3">
            <Confetti size={22} weight="bold" />
            <span className="font-bold text-sm tracking-widest">BADGES</span>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <motion.form
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          onSubmit={submit}
          className="brut w-full max-w-md p-8 bg-white"
          data-testid="invite-form"
        >
          <div className="flex items-center gap-2 mb-1">
            <UserPlus size={22} weight="bold" />
            <span className="font-display font-black text-xl tracking-tight">CREATE YOUR ACCOUNT</span>
          </div>
          <p className="text-zinc-600 mb-6 text-sm">
            You'll be auto-assigned to <span className="font-bold">{preview.mp_name}</span>'s downline.
          </p>

          <div className="space-y-4">
            <div>
              <Label className="font-bold uppercase tracking-widest text-xs">Full Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="mt-2 border-2 border-black rounded-none h-12 focus-visible:ring-0 focus:shadow-[4px_4px_0_0_#09090b]"
                placeholder="Jane Smith"
                data-testid="invite-name"
              />
            </div>
            <div>
              <Label className="font-bold uppercase tracking-widest text-xs">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="mt-2 border-2 border-black rounded-none h-12 focus-visible:ring-0 focus:shadow-[4px_4px_0_0_#09090b]"
                placeholder="jane@cfn.com"
                data-testid="invite-email"
              />
            </div>
            <div>
              <Label className="font-bold uppercase tracking-widest text-xs">Password</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                minLength={6}
                className="mt-2 border-2 border-black rounded-none h-12 focus-visible:ring-0 focus:shadow-[4px_4px_0_0_#09090b]"
                placeholder="Min 6 characters"
                data-testid="invite-password"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-none bg-[#2563eb] hover:bg-[#1d4ed8] text-white border-2 border-black font-display font-black text-base tracking-wide shadow-[4px_4px_0_0_#09090b] hover:shadow-[6px_6px_0_0_#09090b] hover:-translate-y-0.5 active:shadow-none active:translate-y-1 active:translate-x-1 transition-all"
              data-testid="invite-submit"
            >
              {submitting ? "CREATING…" : "JOIN THE TEAM →"}
            </Button>
          </div>

          <div className="mt-6 pt-5 border-t-2 border-dashed border-zinc-300 text-xs text-zinc-500 text-center">
            Already have a CFN account? <Link to="/login" className="font-bold underline hover:text-black">Sign in</Link>
          </div>
        </motion.form>
      </div>
    </div>
  );
}
